import { z } from 'zod';

export const DepartmentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Department = z.infer<typeof DepartmentSchema>;

const code = z
  .string()
  .min(1)
  .max(20)
  .regex(/^[A-Za-z0-9_-]+$/, 'Letters, digits, underscore, hyphen only');

const DepartmentBody = z.object({
  code,
  name: z.string().min(1).max(120),
  active: z.boolean().optional(),
});

export const CreateDepartmentInput = DepartmentBody;
export type CreateDepartmentInput = z.infer<typeof CreateDepartmentInput>;

export const PatchDepartmentInput = DepartmentBody.partial();
export type PatchDepartmentInput = z.infer<typeof PatchDepartmentInput>;

export interface ListDepartmentsQuery {
  q?: string;
  active?: boolean;
  sortField?: 'name' | 'code' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Export query. Single source of truth so a new list filter
 * automatically participates in export by adding one optional field
 * here (and the matching field on the backend's `exportQuerySchema`).
 * Subset of `ListDepartmentsQuery` minus pagination — exports drain
 * the whole filtered result.
 */
export type ExportDepartmentsQuery = Pick<
  ListDepartmentsQuery,
  'active' | 'q' | 'sortField' | 'sortDirection'
>;

/** Result of POST /api/departments/import — one entry per row in the
 *  uploaded file (in the same order). `outcome` reflects whether the
 *  row was created, updated by upsert-on-code, or rejected. */
export type ImportDepartmentRowOutcome = 'created' | 'updated' | 'unchanged' | 'failed';

/**
 * Structured per-row error. `field` (when present) is the column the
 * error relates to; the UI can use it to anchor the message next to
 * the offending column header. `code` is a coarse machine-readable
 * tag so future automation can branch on it.
 */
export type ImportDepartmentErrorCode =
  | 'REQUIRED'
  | 'INVALID_FORMAT'
  | 'TOO_LONG'
  | 'DUPLICATE_IN_FILE'
  | 'CONFLICT'
  | 'INTERNAL';

export interface ImportDepartmentRowError {
  code: ImportDepartmentErrorCode;
  message: string;
  field?: 'code' | 'name' | 'active';
}

export interface ImportDepartmentsRow {
  rowNumber: number;
  code: string | null;
  outcome: ImportDepartmentRowOutcome;
  errors?: ImportDepartmentRowError[];
}

export interface ImportDepartmentsResult {
  totalRows: number;
  created: number;
  /** Rows that matched an existing dept AND the file's values
   *  differed from on-disk; produced an audit entry. */
  updated: number;
  /** Rows that matched an existing dept but were no-ops (file values
   *  equal on-disk values). No audit entry. Reported so re-importing
   *  an unedited export doesn't masquerade as N successful updates. */
  unchanged: number;
  failed: number;
  rows: ImportDepartmentsRow[];
}
